import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { IFinalStepData } from "@/pages/Registration";
import { useRegisterMutation } from "@/data/user.ts";
import { FindUserResponse, ILanguages, RegisterUserInput } from "@/types";
import type { SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import Form from "@/components/ui/forms/Form.tsx";
import dayjs from "dayjs";
import AuthContent from "@edusogno/layouts/auth-content.js";
import RegistrationEmail from "@/components/auth/registration/forms/Email.tsx";
import RegistrationChannel from "@/components/auth/registration/forms/Channel.tsx";
import RegistrationLanguage from "@/components/auth/registration/forms/Language.tsx";
import RegistrationPassword from "@/components/auth/registration/forms/Password.tsx";
import RegistrationStartDate from "@/components/auth/registration/forms/Date.tsx";
import RegistrationProfession from "@/components/auth/registration/forms/Profession.tsx";
import RegistrationDateOfBirth from "@/components/auth/registration/forms/DateOfBirth.tsx";

interface RegistrationFormsProps {
  step: number;
  user: FindUserResponse | undefined;
  setStep: (value: (r: number) => number) => void;
  disabled: boolean;
  nextStep: () => void;
  hasOneToOne: boolean;
  setDisabled?: (value: boolean) => void;
  setDataFinalStep: Dispatch<SetStateAction<IFinalStepData | undefined>>;
}

const RegistrationForms = ({
  step,
  user,
  setStep,
  nextStep,
  hasOneToOne,
  setDataFinalStep,
}: RegistrationFormsProps) => {
  const { t } = useTranslation();

  const registrationValidationSchema = yup.object().shape({
    lang: yup.string().required(t("auth.errors.language_required")),
    birth_date: yup.string().required(t("auth.errors.dob_required")),
    password: yup
      .string()
      .required(t("auth.errors.password_required"))
      .matches(
        /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]).{8,}$/,
        t("auth.errors.password_invalid"),
      ),
    confirm_password: yup
      .string()
      .oneOf([yup.ref("password"), undefined], t("auth.errors.password_match"))
      .required(t("auth.errors.password_required")),
      profession: yup.string().required(t("auth.errors.profession_required")),
    email: yup
      .string()
      .email(t("auth.errors.valid_email"))
      .required(t("auth.errors.email_required")),
    referer: yup.string().required(t("auth.errors.referer_required")),
    start_month: yup.string().required(t("auth.errors.start_month_required")),
    sector: yup.string(),
    token: yup.string().required(),
    speech_ace_score: yup.string(),
  });

  const { mutate: register, isLoading } = useRegisterMutation(nextStep);

  const onSubmit: SubmitHandler<RegisterUserInput> = (data) => {
    setDataFinalStep({
      selectedLanguage: data?.lang as ILanguages,
      selectedStartMonth: data?.start_month as string,
    });
    register(data);
    nextStep();
  };
  const onNextStep = () => setStep((r) => r + 1);

  return (
    <>
      <Form<RegisterUserInput>
        onSubmit={onSubmit}
        validationSchema={registrationValidationSchema}
        id={"login-form"}
        useFormProps={{
          defaultValues: {
            referer: "instagram",
            profession: "studente",
            email: user ? user.email : "",
            start_month: !hasOneToOne ? user?.start_month : undefined,
            birth_date: dayjs().subtract(18, "years").format("YYYY-MM-DD"),
            lang: user ? user.lang : "it",
            token: user?.token || "",
          },
          reValidateMode: "onBlur",
          mode: "onBlur",
        }}
      >
        {({ getValues }) => (
          <>
            <AuthContent className={"py-0 gap-0 flex- !px-0  h-full w-full"}>
              {/*<Watcher*/}
              {/*  step={step}*/}
              {/*  hasOneToOne={hasOneToOne}*/}
              {/*  setDisabled={setDisabled}*/}
              {/*/>*/}
              {/*<RegistrationLanguage className={step !== 0 ? "hidden" : ""} />*/}
              {/*<RegistrationDateOfBirth className={step !== 1 ? "hidden" : ""} />*/}
              {/*<RegistrationProfession className={step !== 2 ? "hidden" : ""} />*/}
              {/*<RegistrationChannel className={step !== 3 ? "hidden" : ""} />*/}
              {/*<RegistrationPassword className={step !== 4 ? "hidden" : ""} />*/}
              {/*<RegistrationEmail className={step !== 5 ? "hidden" : ""} />*/}
              {/*<RegistrationStartDate*/}
              {/*  dates={user?.init_dates || []}*/}
              {/*  className={step !== 6 || hasOneToOne ? "hidden" : ""}*/}
              {/*/>*/}
              {step == 0 ? <RegistrationLanguage onNext={onNextStep} /> : <></>}
              {step == 1 ? (
                <RegistrationDateOfBirth onNext={onNextStep} />
              ) : (
                <></>
              )}
              {step == 2 ? (
                <RegistrationProfession onNext={onNextStep} />
              ) : (
                <></>
              )}
              {step == 3 ? <RegistrationChannel onNext={onNextStep} /> : <></>}
              {step == 4 ? <RegistrationPassword onNext={onNextStep} /> : <></>}
              {step == 5 ? (
                <RegistrationEmail
                  isLoading={isLoading}
                  onNext={() => {
                    {
                      if (!hasOneToOne) {
                        onNextStep();
                      } else {
                        onSubmit(getValues());
                      }
                    }
                  }}
                />
              ) : (
                <></>
              )}
              {!(step !== 6 || hasOneToOne) ? (
                <RegistrationStartDate
                  dates={user?.init_dates || []}
                  onNext={() => onSubmit(getValues())}
                />
              ) : (
                <></>
              )}
              {/* {step === 7 ? <EnglishTest onNext={nextStep} /> : <></>} */}
              {/* {step === 8 ? <SpeechAce onNext={nextStep} /> : <></>} */}
            </AuthContent>
          </>
        )}
      </Form>
    </>
  );
};

export default RegistrationForms;
